import React from "react";
import { ResponsivePie } from "@nivo/pie";
import { formatNumberWithCommas } from "functions";

// One color per route, gold first so the busiest page carries the brand.
const ROUTE_COLORS = [
  "#d3963a", // gold
  "#c64949", // red
  "#7aa67a", // sage
  "#6b95c4", // blue
  "#b07cc4", // lavender
  "#d68d5b", // burnt orange
  "#c4a06b", // tan
  "#5e9b9b", // teal
];

// Show this many routes individually; the rest fold into "Other".
const TOP_ROUTES = 7;

// Donut of unique visitors per route. Percentages answer "of the range's
// total unique visitors, what share viewed this page" — so 64% on /menu
// means 64% of all visitors opened the menu. They can sum past 100 because
// one visitor usually views several pages; slice sizes stay proportional.
function RoutesPie({ pages, totalVisitors }) {
  const ranked = [...(pages || [])].sort((a, b) => b.visitors - a.visitors);
  if (ranked.length === 0 || !totalVisitors) {
    return (
      <div style={{ opacity: 0.55, fontSize: "14px" }}>
        No traffic in this range yet.
      </div>
    );
  }

  const top = ranked.slice(0, TOP_ROUTES);
  const rest = ranked.slice(TOP_ROUTES);
  const entries = top.map((p) => ({ label: p.route, visitors: p.visitors }));
  if (rest.length > 0) {
    entries.push({
      label: "Other",
      visitors: rest.reduce((sum, p) => sum + p.visitors, 0),
    });
  }

  const pct = (n) => Math.round((n / totalVisitors) * 100);
  const data = entries.map((e, i) => ({
    id: e.label,
    value: e.visitors,
    color: ROUTE_COLORS[i % ROUTE_COLORS.length],
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      <div className="pointer-events" style={{ height: "220px" }}>
        <ResponsivePie
          data={data}
          margin={{ top: 16, right: 16, bottom: 16, left: 16 }}
          innerRadius={0.6}
          padAngle={1.5}
          cornerRadius={3}
          colors={{ datum: "data.color" }}
          tooltip={({ datum }) => (
            <div
              style={{
                background: "#111115",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: "8px",
                padding: "9px 13px",
                fontSize: "13px",
                color: "#f0ede8",
                pointerEvents: "none",
                boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                gap: "7px",
                whiteSpace: "nowrap",
              }}
            >
              <span
                style={{
                  display: "inline-block",
                  width: 9,
                  height: 9,
                  borderRadius: 2,
                  background: datum.color,
                  flexShrink: 0,
                }}
              />
              <span style={{ opacity: 0.8 }}>{datum.id}</span>
              <span style={{ fontWeight: 700 }}>
                {formatNumberWithCommas(datum.value)}
              </span>
              <span style={{ opacity: 0.55, fontSize: "12px" }}>
                ({pct(datum.value)}% of visitors)
              </span>
            </div>
          )}
          enableArcLinkLabels={false}
          arcLabel={(d) => `${pct(d.value)}%`}
          arcLabelsSkipAngle={25}
          arcLabelsTextColor="#111115"
          theme={{ labels: { text: { fontSize: 13, fontWeight: 700 } } }}
        />
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {data.map((d) => (
          <div
            key={d.id}
            style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}
          >
            <span
              style={{
                display: "inline-block",
                width: 9,
                height: 9,
                borderRadius: 2,
                background: d.color,
                flexShrink: 0,
              }}
            />
            <span
              style={{
                opacity: 0.8,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {d.id}
            </span>
            <span style={{ marginLeft: "auto", fontWeight: 700 }}>
              {formatNumberWithCommas(d.value)}
            </span>
            <span style={{ opacity: 0.55, fontSize: "12px", width: 38, textAlign: "right" }}>
              {pct(d.value)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default RoutesPie;
